<template>
  <HrPageLayout
    title="Disciplinary & HR Actions"
    subtitle="Warnings, show cause, suspension, salary stoppage, commendation and other HR actions — recorded on employee history"
    icon="⚖️"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Public Service Board Module' },
      { label: 'Disciplinary & HR Action' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" @click="openTimelineModal()">
        <i class="bx bx-time-five"></i> Employee timeline
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="openActionModal()">
        <i class="bx bx-plus"></i> Add HR Action
      </button>
    </template>

    <div class="dha-page">
      <div class="dha-metrics">
        <div class="dha-metric">
          <span>Records</span>
          <strong>{{ pagination.total }}</strong>
        </div>
        <div class="dha-metric dha-metric-warn">
          <span>Page</span>
          <strong>{{ pagination.page }} / {{ pagination.totalPages }}</strong>
        </div>
      </div>

      <div class="dha-filters">
        <label class="dha-field dha-wide">
          <span>Search employee</span>
          <div class="dha-search">
            <i class="bx bx-search"></i>
            <input v-model="filters.search" type="search" placeholder="Name, emp No, ID, issued by…" @input="debouncedLoad">
          </div>
        </label>
        <label class="dha-field">
          <span>Department</span>
          <select v-model="filters.departmentName" @change="onFilterChange">
            <option value="">All departments</option>
            <option v-for="d in departments" :key="d" :value="d">{{ d }}</option>
          </select>
        </label>
        <label class="dha-field">
          <span>Action type</span>
          <select v-model="filters.action_type" @change="onFilterChange">
            <option value="">All types</option>
            <option v-for="t in actionTypes" :key="t" :value="t">{{ t }}</option>
          </select>
        </label>
        <label class="dha-field">
          <span>From</span>
          <input v-model="filters.fromDate" type="date" @change="onFilterChange">
        </label>
        <label class="dha-field">
          <span>To</span>
          <input v-model="filters.toDate" type="date" @change="onFilterChange">
        </label>
        <label class="dha-field">
          <span>Per page</span>
          <select v-model.number="pagination.limit" @change="onFilterChange">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </label>
        <div class="dha-field dha-actions">
          <button type="button" class="hr-btn hr-btn-ghost" @click="resetFilters">Clear</button>
          <button type="button" class="hr-btn hr-btn-outline-primary" @click="loadActions"><i class="bx bx-refresh"></i></button>
        </div>
      </div>

      <div class="dha-card">
        <div v-if="loading" class="dha-loading">
          <div class="dha-spinner"></div>
          Loading HR actions…
        </div>
        <div v-else-if="!actions.length" class="dha-empty">
          <i class="bx bx-folder-open"></i>
          <h5>No actions found</h5>
          <p>Adjust filters or add a new HR action.</p>
        </div>
        <div v-else class="dha-table-wrap">
          <table class="dha-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>Department</th>
                <th>Action type</th>
                <th>Date</th>
                <th>Description</th>
                <th>Document</th>
                <th>Issued by</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(a, idx) in actions" :key="a.id">
                <td class="muted">{{ rowNum(idx) }}</td>
                <td>
                  <div class="dha-emp">
                    <span class="dha-avatar">{{ initials(a.firstName, a.lastName) }}</span>
                    <div>
                      <strong>{{ a.firstName }} {{ a.lastName }}</strong>
                      <small>{{ a.empNo }}</small>
                    </div>
                  </div>
                </td>
                <td>{{ a.department_name || a.emp_dept || '—' }}</td>
                <td>
                  <span class="dha-type" :class="getActionClass(a.action_type)">{{ a.action_type }}</span>
                </td>
                <td>{{ a.action_date }}</td>
                <td class="dha-desc">{{ (a.description || '').slice(0, 70) }}{{ (a.description || '').length > 70 ? '…' : '' }}</td>
                <td>
                  <button v-if="a.document_attachment" type="button" class="link-btn" @click="viewAttachment(a)">View PDF</button>
                  <span v-else class="muted">—</span>
                </td>
                <td>{{ a.issued_by || '—' }}</td>
                <td>
                  <span class="status-pill" :class="a.status === 'Active' ? 'ok' : 'closed'">{{ a.status }}</span>
                </td>
                <td class="row-actions">
                  <button type="button" class="hr-btn hr-btn-sm" @click="editAction(a)">Edit</button>
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="showEmployeeTimeline(a.employee_id)">Timeline</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!loading && pagination.total > 0" class="dha-pagination">
          <span>Showing {{ pageFrom }}–{{ pageTo }} of {{ pagination.total }}</span>
          <div class="dha-page-btns">
            <button type="button" :disabled="!pagination.hasPrev" @click="goPage(1)">«</button>
            <button type="button" :disabled="!pagination.hasPrev" @click="goPage(pagination.page - 1)">Prev</button>
            <span>Page {{ pagination.page }} / {{ pagination.totalPages }}</span>
            <button type="button" :disabled="!pagination.hasNext" @click="goPage(pagination.page + 1)">Next</button>
            <button type="button" :disabled="!pagination.hasNext" @click="goPage(pagination.totalPages)">»</button>
          </div>
        </div>
      </div>

      <div v-if="showActionModal" class="modal-overlay" @click.self="showActionModal = false">
        <div class="modal-card">
          <div class="modal-head">
            <h3>{{ editingId ? 'Edit' : 'Add' }} HR action</h3>
            <button type="button" class="x" @click="showActionModal = false">&times;</button>
          </div>
          <div class="modal-form-grid">
            <div class="form-group full">
              <label>Employee</label>
              <div v-if="editingId" class="emp-selected compact">
                <strong>{{ formEmployeeLabel }}</strong>
              </div>
              <div v-else class="emp-typeahead">
                <i class="bx bx-search"></i>
                <input
                  v-model="formEmpSearch"
                  type="search"
                  placeholder="Search employee by name or PF no…"
                  autocomplete="off"
                  @input="onFormEmpSearch"
                  @focus="formEmpShow = true"
                >
              </div>
              <ul v-if="!editingId && formEmpShow && formEmpResults.length" class="emp-drop in-form">
                <li v-for="e in formEmpResults" :key="e.id" @mousedown.prevent="pickFormEmployee(e)">
                  <strong>{{ e.firstName }} {{ e.lastName }}</strong>
                  <small>{{ e.empNo }}</small>
                </li>
              </ul>
              <div v-if="!editingId && form.employee_id" class="emp-selected compact">
                Selected: {{ formEmployeeLabel }}
                <button type="button" class="link-btn" @click="clearFormEmployee">Change</button>
              </div>
            </div>
            <div class="form-group">
              <label>Action type</label>
              <select v-model="form.action_type" required>
                <option value="">Select</option>
                <option v-for="t in actionTypes" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Action date</label>
              <input v-model="form.action_date" type="date" required>
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="form.status">
                <option value="Active">Active</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div class="form-group full">
              <label>Description</label>
              <textarea v-model="form.description" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>Issued by</label>
              <input v-model="form.issued_by" type="text">
            </div>
            <div class="form-group">
              <label>PDF attachment</label>
              <input ref="pdfInput" type="file" accept=".pdf,application/pdf" @change="onPdfSelected">
              <div v-if="form.document_attachment" class="mt-1">
                <button type="button" class="link-btn" @click="viewAttachment(form)">View current PDF</button>
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showActionModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveAction">Save</button>
          </div>
        </div>
      </div>

      <div v-if="showTimelineModal" class="modal-overlay" @click.self="showTimelineModal = false">
        <div class="modal-card modal-card-wide timeline-modal">
          <div class="modal-head">
            <div>
              <h3>Employee HR timeline</h3>
              <p class="modal-sub">Search an employee and view their full disciplinary &amp; HR action history</p>
            </div>
            <button type="button" class="x" @click="showTimelineModal = false">&times;</button>
          </div>

          <div class="timeline-search-block">
            <label>Search employee</label>
            <div class="emp-typeahead">
              <i class="bx bx-search"></i>
              <input
                v-model="timelineSearch"
                type="search"
                placeholder="Type name, PF/emp no, national ID…"
                autocomplete="off"
                @input="onTimelineSearch"
                @focus="timelineShowResults = true"
              >
              <button v-if="timelineEmployeeId || timelineSearch" type="button" class="clear-chip" @click="clearTimelineEmployee">Clear</button>
            </div>
            <ul v-if="timelineShowResults && timelineResults.length" class="emp-drop">
              <li v-for="e in timelineResults" :key="e.id" @mousedown.prevent="pickTimelineEmployee(e)">
                <span class="dha-avatar sm">{{ initials(e.firstName, e.lastName) }}</span>
                <div>
                  <strong>{{ e.firstName }} {{ e.lastName }}</strong>
                  <small>{{ e.empNo }} · {{ e.departmentName || e.department_name || '—' }}</small>
                </div>
              </li>
            </ul>
            <div v-if="timelineEmployeeId" class="emp-selected">
              <span class="dha-avatar">{{ initials(timelinePicked?.firstName, timelinePicked?.lastName) }}</span>
              <div>
                <strong>{{ timelineEmployeeName }}</strong>
                <small>{{ timelinePicked?.departmentName || timelinePicked?.department_name || '' }}</small>
              </div>
            </div>
          </div>

          <div v-if="timelineLoading" class="dha-loading pad-sm"><div class="dha-spinner"></div> Loading timeline…</div>
          <div v-else-if="!timelineEmployeeId" class="timeline-empty-hint">
            <i class="bx bx-search-alt"></i>
            <p>Search and select an employee to load their HR history.</p>
          </div>
          <div v-else class="timeline-list improved">
            <div v-for="item in timelineActions" :key="item.id" class="timeline-item improved">
              <div class="timeline-marker" :class="getActionClass(item.action_type)"></div>
              <div class="timeline-content cardish">
                <div class="tl-top">
                  <span class="dha-type" :class="getActionClass(item.action_type)">{{ item.action_type }}</span>
                  <time>{{ item.action_date }}</time>
                </div>
                <div v-if="item.description" class="timeline-desc">{{ item.description }}</div>
                <div class="timeline-meta">
                  Issued by: {{ item.issued_by || '–' }}
                  <span v-if="item.created_at"> · recorded {{ item.created_at }}</span>
                  <span v-if="item.status"> · {{ item.status }}</span>
                </div>
                <button v-if="item.document_attachment" type="button" class="link-btn" @click="viewAttachment(item)">View attachment</button>
              </div>
            </div>
            <div v-if="!timelineActions.length" class="timeline-empty-hint">
              <i class="bx bx-folder-open"></i>
              <p>No HR actions recorded for this employee yet.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, executeDownload, authUrl, toast } from "@/api";

const ACTION_TYPES = [
  "Warning",
  "Show Cause Letter",
  "Suspension",
  "Interdiction",
  "Termination",
  "Salary Stoppage",
  "Commendation",
  "Promotion",
  "Transfer"
];

export default {
  name: "DisciplinaryHrActions",
  components: { HrPageLayout },
  data() {
    return {
      actionTypes: ACTION_TYPES,
      loading: false,
      filters: {
        action_type: "",
        departmentName: "",
        search: "",
        employeeId: "",
        fromDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        toDate: new Date().toISOString().slice(0, 10)
      },
      pagination: { page: 1, limit: 25, total: 0, totalPages: 1, hasPrev: false, hasNext: false },
      actions: [],
      employees: [],
      departments: [],
      debounceTimer: null,
      showActionModal: false,
      showTimelineModal: false,
      editingId: null,
      saving: false,
      timelineEmployeeId: "",
      timelineActions: [],
      timelineSearch: "",
      timelineResults: [],
      timelineShowResults: false,
      timelineLoading: false,
      timelinePicked: null,
      formEmpSearch: "",
      formEmpResults: [],
      formEmpShow: false,
      form: {
        id: null,
        employee_id: "",
        action_type: "",
        action_date: new Date().toISOString().slice(0, 10),
        description: "",
        issued_by: "",
        document_attachment: "",
        status: "Active"
      },
      selectedPdfFile: null
    };
  },
  computed: {
    timelineEmployeeName() {
      if (this.timelinePicked) {
        return `${this.timelinePicked.firstName} ${this.timelinePicked.lastName} (${this.timelinePicked.empNo || ""})`;
      }
      if (!this.timelineEmployeeId) return "";
      const e = this.employees.find((x) => x.id == this.timelineEmployeeId);
      return e ? `${e.firstName} ${e.lastName} (${e.empNo})` : "";
    },
    formEmployeeLabel() {
      const e = this.employees.find((x) => x.id == this.form.employee_id);
      return e ? `${e.firstName} ${e.lastName} (${e.empNo})` : String(this.form.employee_id || "");
    },
    pageFrom() {
      if (!this.pagination.total) return 0;
      return (this.pagination.page - 1) * this.pagination.limit + 1;
    },
    pageTo() {
      return Math.min(this.pagination.page * this.pagination.limit, this.pagination.total);
    }
  },
  created() {
    this.loadEmployees();
    this.loadDepartments();
    this.loadActions();
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    rowNum(idx) {
      return (this.pagination.page - 1) * this.pagination.limit + idx + 1;
    },
    initials(a, b) {
      return `${(a || " ")[0]}${(b || " ")[0]}`.toUpperCase();
    },
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.loadActions();
      }, 350);
    },
    onFilterChange() {
      this.pagination.page = 1;
      this.loadActions();
    },
    resetFilters() {
      this.filters = {
        action_type: "",
        departmentName: "",
        search: "",
        employeeId: "",
        fromDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        toDate: new Date().toISOString().slice(0, 10)
      };
      this.pagination.page = 1;
      this.loadActions();
    },
    goPage(p) {
      this.pagination.page = Math.max(1, p);
      this.loadActions();
    },
    loadActions() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "getHrActions");
      fd.append("page", String(this.pagination.page));
      fd.append("limit", String(this.pagination.limit));
      if (this.filters.employeeId) fd.append("employeeId", this.filters.employeeId);
      if (this.filters.action_type) fd.append("action_type", this.filters.action_type);
      if (this.filters.fromDate) fd.append("fromDate", this.filters.fromDate);
      if (this.filters.toDate) fd.append("toDate", this.filters.toDate);
      if (this.filters.departmentName) fd.append("departmentName", this.filters.departmentName);
      if (this.filters.search) fd.append("search", this.filters.search.trim());
      execute(fd, authUrl)
        .then((res) => {
          const d = res.data && res.data.success ? res.data.data || {} : {};
          this.actions = d.actions || [];
          const p = d.pagination || {};
          this.pagination = {
            page: Number(p.page || this.pagination.page),
            limit: Number(p.limit || this.pagination.limit),
            total: Number(p.total || this.actions.length),
            totalPages: Number(p.totalPages || 1),
            hasPrev: !!p.hasPrev,
            hasNext: !!p.hasNext
          };
        })
        .catch(() => {
          this.actions = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("page", "1");
      fd.append("limit", "500");
      execute(fd, authUrl)
        .then((res) => {
          if (res.data && res.data.success && res.data.data && res.data.data.employees) {
            this.employees = res.data.data.employees;
          }
        })
        .catch(() => {});
    },
    loadDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl)
        .then((res) => {
          const payload = res?.data?.data || {};
          let list = payload.departments || [];
          if (!Array.isArray(list)) list = [];
          this.departments = list
            .map((d) => d.departmentName || d.name || "")
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
        })
        .catch(() => {
          this.departments = [];
        });
    },
    filterEmployees(q) {
      const term = String(q || "").trim().toLowerCase();
      if (term.length < 1) return [];
      return (this.employees || [])
        .filter((e) => {
          const blob = [
            e.firstName, e.lastName, e.middleName, e.empNo, e.nationalId, e.idNo,
            e.departmentName, e.department_name
          ].map((x) => String(x || "").toLowerCase()).join(" ");
          return blob.includes(term);
        })
        .slice(0, 12);
    },
    onTimelineSearch() {
      this.timelineShowResults = true;
      this.timelineResults = this.filterEmployees(this.timelineSearch);
    },
    pickTimelineEmployee(e) {
      this.timelinePicked = e;
      this.timelineEmployeeId = String(e.id);
      this.timelineSearch = `${e.firstName} ${e.lastName} (${e.empNo || ""})`;
      this.timelineShowResults = false;
      this.timelineResults = [];
      this.loadEmployeeTimeline();
    },
    clearTimelineEmployee() {
      this.timelinePicked = null;
      this.timelineEmployeeId = "";
      this.timelineSearch = "";
      this.timelineResults = [];
      this.timelineActions = [];
      this.timelineShowResults = false;
    },
    onFormEmpSearch() {
      this.formEmpShow = true;
      this.formEmpResults = this.filterEmployees(this.formEmpSearch);
    },
    pickFormEmployee(e) {
      this.form.employee_id = e.id;
      this.formEmpSearch = `${e.firstName} ${e.lastName} (${e.empNo || ""})`;
      this.formEmpShow = false;
      this.formEmpResults = [];
    },
    clearFormEmployee() {
      this.form.employee_id = "";
      this.formEmpSearch = "";
      this.formEmpResults = [];
      this.formEmpShow = true;
    },
    loadEmployeeTimeline() {
      if (!this.timelineEmployeeId) {
        this.timelineActions = [];
        return;
      }
      this.timelineLoading = true;
      const fd = new FormData();
      fd.append("function", "getEmployeeHrActions");
      fd.append("employeeId", this.timelineEmployeeId);
      execute(fd, authUrl)
        .then((res) => {
          this.timelineActions =
            res.data && res.data.success && res.data.data && res.data.data.actions ? res.data.data.actions : [];
        })
        .catch(() => {
          this.timelineActions = [];
        })
        .finally(() => {
          this.timelineLoading = false;
        });
    },
    openActionModal(record) {
      this.editingId = record ? record.id : null;
      this.selectedPdfFile = null;
      this.formEmpShow = false;
      this.formEmpResults = [];
      if (record) {
        this.form = {
          id: record.id,
          employee_id: record.employee_id,
          action_type: record.action_type,
          action_date: (record.action_date || "").slice(0, 10),
          description: record.description,
          issued_by: record.issued_by,
          document_attachment: record.document_attachment,
          status: record.status || "Active"
        };
        this.formEmpSearch = `${record.firstName || ""} ${record.lastName || ""}`.trim();
      } else {
        this.form = {
          id: null,
          employee_id: "",
          action_type: "",
          action_date: new Date().toISOString().slice(0, 10),
          description: "",
          issued_by: "",
          document_attachment: "",
          status: "Active"
        };
        this.formEmpSearch = "";
      }
      this.$nextTick(() => {
        if (this.$refs.pdfInput) this.$refs.pdfInput.value = "";
      });
      this.showActionModal = true;
    },
    editAction(a) {
      this.openActionModal(a);
    },
    onPdfSelected(e) {
      const file = e?.target?.files?.[0] || null;
      if (!file) {
        this.selectedPdfFile = null;
        return;
      }
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
      if (!isPdf) {
        this.selectedPdfFile = null;
        if (this.$refs.pdfInput) this.$refs.pdfInput.value = "";
        toast.error("Only PDF is allowed");
        return;
      }
      this.selectedPdfFile = file;
    },
    saveAction() {
      if (!this.form.employee_id || !this.form.action_type || !this.form.action_date) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "saveHrAction");
      Object.keys(this.form).forEach((k) => fd.append(k, this.form[k] == null ? "" : this.form[k]));
      if (this.selectedPdfFile) fd.append("document_file", this.selectedPdfFile);
      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            this.showActionModal = false;
            this.loadActions();
            if (this.showTimelineModal && this.timelineEmployeeId == this.form.employee_id) {
              this.loadEmployeeTimeline();
            }
          } else toast.error(res.data && res.data.message ? res.data.message : "Save failed");
        })
        .catch(() => {
          this.saving = false;
        });
    },
    async viewAttachment(a) {
      const id = a?.id || this.form?.id;
      if (!id) return;
      try {
        const data = new FormData();
        data.append("function", "downloadHrActionAttachment");
        data.append("id", String(id));
        const response = await executeDownload(data, authUrl);
        const blob = response?.data;
        if (!(blob instanceof Blob)) {
          toast.error("Could not open attachment");
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        toast.error("Could not open attachment");
      }
    },
    openTimelineModal() {
      this.clearTimelineEmployee();
      this.showTimelineModal = true;
    },
    showEmployeeTimeline(employeeId) {
      const e = this.employees.find((x) => String(x.id) === String(employeeId));
      if (e) this.pickTimelineEmployee(e);
      else {
        this.timelineEmployeeId = String(employeeId);
        this.timelinePicked = null;
        this.timelineSearch = "";
        this.loadEmployeeTimeline();
      }
      this.showTimelineModal = true;
    },
    getActionClass(type) {
      const t = (type || "").toLowerCase();
      if (
        t.includes("warning") ||
        t.includes("show cause") ||
        t.includes("suspension") ||
        t.includes("interdiction") ||
        t.includes("termination") ||
        t.includes("salary stop")
      ) {
        return "action-disciplinary";
      }
      if (t.includes("commendation") || t.includes("promotion") || t.includes("transfer")) return "action-positive";
      return "action-neutral";
    }
  }
};
</script>

<style scoped>
.dha-page { display: flex; flex-direction: column; gap: 1rem; }
.dha-metrics { display: flex; gap: 0.75rem; flex-wrap: wrap; }
.dha-metric {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 0.8rem 1.1rem; min-width: 120px;
}
.dha-metric span { display: block; font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
.dha-metric strong { font-size: 1.25rem; color: #0f172a; }
.dha-filters {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 0.9fr 0.9fr 0.7fr auto;
  gap: 0.65rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  align-items: end;
}
.dha-field { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.72rem; font-weight: 700; color: #64748b; }
.dha-field input, .dha-field select {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.5rem 0.65rem;
  background: #f8fafc; font-size: 0.88rem; color: #0f172a;
}
.dha-search { position: relative; }
.dha-search i { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.dha-search input { width: 100%; padding-left: 32px; }
.dha-actions { display: flex; gap: 0.4rem; }
.dha-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden; }
.dha-loading, .dha-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3rem; color: #64748b; gap: 0.5rem; }
.dha-spinner { width: 24px; height: 24px; border: 3px solid #e2e8f0; border-top-color: #0c4a6e; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.dha-empty i { font-size: 2.5rem; color: #94a3b8; }
.dha-table-wrap { overflow-x: auto; }
.dha-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
.dha-table th {
  background: #f8fafc; text-align: left; padding: 0.7rem 0.85rem;
  font-size: 0.7rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; border-bottom: 1px solid #e2e8f0;
}
.dha-table td { padding: 0.75rem 0.85rem; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.dha-table tr:hover td { background: #f8fafc; }
.dha-emp { display: flex; align-items: center; gap: 0.55rem; }
.dha-avatar {
  width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg,#0c4a6e,#0369a1);
  color: #fff; font-size: 0.7rem; font-weight: 700; display: flex; align-items: center; justify-content: center;
}
.dha-emp strong { display: block; font-size: 0.88rem; }
.dha-emp small { color: #64748b; font-family: monospace; }
.dha-type {
  display: inline-block; padding: 0.2rem 0.55rem; border-radius: 999px; font-size: 0.75rem; font-weight: 700;
}
.dha-type.action-disciplinary { background: #fef2f2; color: #b91c1c; }
.dha-type.action-positive { background: #ecfdf5; color: #047857; }
.dha-type.action-neutral { background: #f1f5f9; color: #475569; }
.status-pill { padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; }
.status-pill.ok { background: #eff6ff; color: #1d4ed8; }
.status-pill.closed { background: #f1f5f9; color: #64748b; }
.dha-desc { max-width: 200px; color: #475569; }
.row-actions { display: flex; gap: 0.35rem; white-space: nowrap; }
.link-btn { background: none; border: none; color: #0284c7; font-weight: 600; cursor: pointer; padding: 0; }
.muted { color: #94a3b8; }
.dha-pagination {
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.75rem;
  padding: 0.85rem 1rem; border-top: 1px solid #f1f5f9; font-size: 0.85rem; font-weight: 600; color: #64748b;
}
.dha-page-btns { display: flex; gap: 0.35rem; align-items: center; }
.dha-page-btns button {
  border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 0.35rem 0.6rem; cursor: pointer;
}
.dha-page-btns button:disabled { opacity: 0.4; cursor: not-allowed; }
.modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.45); display: flex; align-items: center; justify-content: center; z-index: 1100; }
.modal-card { background: #fff; border-radius: 14px; padding: 0; max-width: 560px; width: 94%; max-height: 92vh; overflow-y: auto; box-shadow: 0 20px 50px rgba(15,23,42,0.2); }
.modal-card-wide { max-width: 680px; }
.modal-head { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid #e2e8f0; }
.modal-head h3 { margin: 0; font-size: 1.05rem; }
.modal-head .x { border: none; background: none; font-size: 1.5rem; color: #64748b; cursor: pointer; }
.modal-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; padding: 1.1rem 1.25rem; }
.form-group { margin: 0; }
.form-group.full { grid-column: 1 / -1; }
.form-group label { display: block; font-weight: 700; font-size: 0.78rem; color: #475569; margin-bottom: 0.3rem; }
.form-group input, .form-group select, .form-group textarea {
  width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.55rem 0.7rem; background: #f8fafc;
}
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; padding: 0 1.25rem 1.1rem; }
.modal-sub { margin: 0.25rem 0 0; font-size: 0.82rem; color: #64748b; font-weight: 500; }
.timeline-search-block { padding: 1rem 1.25rem 0.5rem; position: relative; }
.timeline-search-block > label { display: block; font-size: 0.75rem; font-weight: 700; color: #475569; margin-bottom: 0.35rem; }
.emp-typeahead { position: relative; }
.emp-typeahead i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.emp-typeahead input {
  width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.65rem 4.5rem 0.65rem 2.25rem;
  background: #f8fafc; font-size: 0.92rem;
}
.clear-chip {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  border: none; background: #e2e8f0; border-radius: 6px; padding: 0.25rem 0.5rem; font-size: 0.72rem; cursor: pointer;
}
.emp-drop {
  list-style: none; margin: 0.35rem 0 0; padding: 0.35rem; background: #fff; border: 1px solid #e2e8f0;
  border-radius: 10px; max-height: 220px; overflow: auto; box-shadow: 0 12px 30px rgba(15,23,42,0.12); z-index: 5; position: absolute; left: 1.25rem; right: 1.25rem;
}
.emp-drop.in-form { position: relative; left: 0; right: 0; margin-top: 0.35rem; }
.emp-drop li {
  display: flex; gap: 0.55rem; align-items: center; padding: 0.55rem 0.65rem; border-radius: 8px; cursor: pointer;
}
.emp-drop li:hover { background: #f1f5f9; }
.emp-drop strong { display: block; font-size: 0.88rem; }
.emp-drop small { color: #64748b; font-size: 0.75rem; }
.emp-selected {
  display: flex; gap: 0.65rem; align-items: center; margin-top: 0.75rem;
  padding: 0.75rem; background: linear-gradient(135deg,#f0f9ff,#eff6ff); border: 1px solid #bae6fd; border-radius: 12px;
}
.emp-selected.compact { margin-top: 0.4rem; padding: 0.5rem 0.65rem; }
.emp-selected strong { display: block; }
.emp-selected small { color: #0369a1; font-size: 0.78rem; }
.dha-avatar.sm { width: 28px; height: 28px; font-size: 0.65rem; }
.timeline-list { margin: 0.5rem 1.25rem 1.25rem; padding-left: 1.25rem; border-left: 2px solid #e2e8f0; }
.timeline-list.improved { border-left-color: #cbd5e1; max-height: 48vh; overflow: auto; padding-right: 0.5rem; }
.timeline-item { position: relative; margin-bottom: 1rem; }
.timeline-item.improved { margin-bottom: 0.85rem; }
.timeline-marker { position: absolute; left: -1.55rem; top: 4px; width: 12px; height: 12px; border-radius: 50%; box-shadow: 0 0 0 3px #fff; }
.timeline-marker.action-disciplinary { background: #dc2626; }
.timeline-marker.action-positive { background: #059669; }
.timeline-marker.action-neutral { background: #64748b; }
.timeline-content.cardish {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.75rem 0.9rem;
}
.tl-top { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem; }
.tl-top time { font-size: 0.78rem; color: #64748b; font-weight: 600; }
.timeline-desc { margin-top: 0.25rem; color: #475569; font-size: 0.9rem; }
.timeline-meta { font-size: 0.75rem; color: #94a3b8; margin-top: 0.35rem; }
.timeline-empty-hint {
  text-align: center; padding: 2rem 1rem; color: #94a3b8;
}
.timeline-empty-hint i { font-size: 2rem; display: block; margin-bottom: 0.35rem; }
.pad-sm { padding: 1.5rem !important; }
.mt-1 { margin-top: 4px; }
.timeline-modal { max-width: 720px; }
.hr-btn-ghost { background: #f8fafc; border: 1px solid #e2e8f0; color: #475569; border-radius: 8px; padding: 0.45rem 0.75rem; cursor: pointer; }
@media (max-width: 1100px) {
  .dha-filters { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 640px) {
  .dha-filters { grid-template-columns: 1fr; }
  .modal-form-grid { grid-template-columns: 1fr; }
}
</style>
